/**
 * Border Decoration definitions for children's book illustrations.
 *
 * This file contains both FE-safe data (title, description, emoji) and BE-only data (prompts).
 * - FE should import: BORDER_STYLE_OPTIONS, BORDER_STYLE_DISPLAY
 * - BE should import: BORDER_STYLES (includes prompts)
 */

/**
 * Border style keys - used for type safety and as enum values.
 * no_border is first as the default option for full-bleed illustrations.
 */
export const BORDER_STYLE_KEYS = [
  'no_border',
  'simple_line_frame',
  'vine_leaves',
  'stars_clouds',
  'geometric_shapes',
  'vintage_ornamental',
  'scalloped_waves',
  'rainbow_stripes',
  'hand_drawn_doodle',
] as const;

export type BorderStyleKey = (typeof BORDER_STYLE_KEYS)[number];

/**
 * Full border style definition including prompts (BE use only).
 */
export interface BorderStyleDefinition {
  /** Display title for the border style */
  title: string;
  /** User-friendly description */
  description: string;
  /** Emoji icon for UI display */
  emoji: string;
  /** AI prompt fragment for border generation (BE only) */
  borderPrompt: string;
  /** Suggested themes this border works best with */
  bestFor: string[];
}

/**
 * FE-safe display data (no prompts).
 */
export interface BorderStyleDisplay {
  title: string;
  description: string;
  emoji: string;
}

/**
 * Complete border style definitions with prompts.
 * Use this on the backend for image generation.
 */
export const BORDER_STYLES: Record<BorderStyleKey, BorderStyleDefinition> = {
  no_border: {
    title: 'No Border',
    description:
      'Full-bleed illustration with no decorative frame or border. Clean edge-to-edge artwork that maximizes the illustration space. Best for immersive, modern designs.',
    emoji: '🖼️',
    borderPrompt:
      'full bleed illustration, no border, no frame, no decorative edges, no corners, solid edge-to-edge artwork, clean unframed composition, maximum illustration space,',
    bestFor: [
      'modern styles',
      'immersive scenes',
      'full-page spreads',
      'minimalist design',
    ],
  },

  simple_line_frame: {
    title: 'Simple Line Frame',
    description:
      'Clean, minimal border with a single or double line creating a classic picture frame effect. Elegant and non-distracting, lets the illustration be the focus.',
    emoji: '⬜',
    borderPrompt:
      'very thin simple line frame border, clean single line rectangle at exact image edge, minimal elegant frame maximum 5% of image width, classic picture book border, thin consistent stroke, no margin or padding from edge,',
    bestFor: ['modern styles', 'text-heavy pages', 'professional appearance'],
  },

  vine_leaves: {
    title: 'Vine & Leaves',
    description:
      'Organic, flowing vine with leaves wrapping around the page edges. Natural and whimsical, adds movement and life to illustrations. Can include flowers or berries.',
    emoji: '🌿',
    borderPrompt:
      'thin delicate vine and leaves border decoration at exact image edge, organic flowing vines maximum 10% of image width, green leaf wreath frame touching image boundary, botanical border design, nature-inspired thin frame, no margin from edge,',
    bestFor: [
      'nature stories',
      'garden themes',
      'fairy tales',
      'forest adventures',
    ],
  },

  stars_clouds: {
    title: 'Stars & Clouds',
    description:
      'Playful scattered stars, moons, and fluffy clouds creating a dreamy frame. Light and airy, perfect for magical and nighttime stories.',
    emoji: '⭐',
    borderPrompt:
      'thin stars and clouds border decoration at exact image edge, small scattered star shapes maximum 10% border width, crescent moon accents, small fluffy cloud corners touching edge, dreamy sky thin frame, no margin or padding,',
    bestFor: [
      'bedtime stories',
      'dream sequences',
      'magical adventures',
      'space themes',
    ],
  },

  geometric_shapes: {
    title: 'Geometric Shapes',
    description:
      'Modern pattern of triangles, circles, and squares creating a contemporary frame. Bold and graphic, appeals to design-conscious parents and older children.',
    emoji: '🔷',
    borderPrompt:
      'thin geometric shapes border decoration at exact image edge, small modern triangle pattern maximum 10% border width, circle and square motifs touching boundary, contemporary thin frame design, no margin from edge,',
    bestFor: ['educational books', 'modern stories', 'counting/shapes books'],
  },

  vintage_ornamental: {
    title: 'Vintage Ornamental',
    description:
      'Classic decorative scrollwork with flourishes and ornate corner designs. Timeless and elegant, evokes traditional fairy tale collections and heirloom books.',
    emoji: '📜',
    borderPrompt:
      'thin vintage ornamental border decoration at exact image edge, delicate scrollwork design maximum 10% border width, small ornate corner flourishes touching boundary, elegant thin filigree edges, no margin or padding from edge,',
    bestFor: [
      'classic fairy tales',
      'heritage stories',
      'special editions',
      'grandparent gifts',
    ],
  },

  scalloped_waves: {
    title: 'Scalloped Waves',
    description:
      'Soft, repeating scallop pattern creating a gentle wave-like frame. Charming and friendly, adds visual interest without overwhelming the illustration.',
    emoji: '🌊',
    borderPrompt:
      'thin scalloped wave border decoration at exact image edge, small soft repeating scallops maximum 10% border width, gentle wave pattern thin frame touching boundary, charming curved edges, no margin from edge,',
    bestFor: [
      'ocean stories',
      'gentle narratives',
      'younger children',
      'peaceful themes',
    ],
  },

  rainbow_stripes: {
    title: 'Rainbow Stripes',
    description:
      'Colorful striped border with gradient rainbow colors. Bold and cheerful, immediately signals fun and celebration. Works well with vibrant illustration styles.',
    emoji: '🌈',
    borderPrompt:
      'thin rainbow stripe border decoration at exact image edge, narrow colorful gradient stripes maximum 10% border width, cheerful rainbow thin frame touching boundary, bold color bands, no margin or padding from edge,',
    bestFor: [
      'celebration themes',
      'birthday books',
      'pride stories',
      'joyful narratives',
    ],
  },

  hand_drawn_doodle: {
    title: 'Hand-Drawn Doodle',
    description:
      'Loose, sketchy doodles and squiggles creating an informal, creative frame. Artistic and spontaneous, appeals to creative children and adds personality.',
    emoji: '✏️',
    borderPrompt:
      'thin hand-drawn doodle border decoration at exact image edge, small sketchy line frame maximum 10% border width, informal squiggle edges touching boundary, creative doodle thin pattern, no margin from edge,',
    bestFor: [
      'creative stories',
      'art themes',
      'informal narratives',
      'activity books',
    ],
  },
};

/**
 * Border style options array for schema validation.
 * Use this in Zod schemas and for iteration.
 */
export const BORDER_STYLE_OPTIONS = BORDER_STYLE_KEYS;

/**
 * Phase 1 plan-aligned alias for `BORDER_STYLE_KEYS`.
 *
 * The architecture-stability plan splits the legacy `pageBorderStyleOverride`
 * into a typed enum (`pageBorderStyleId`) plus a freeform feedback string
 * (`humanCharacterSheetFeedback`). The typed enum reuses the same closed set
 * defined here as `BORDER_STYLE_KEYS`; this alias names it after the field
 * so call-sites read naturally as `pageBorderStyleId: PageBorderStyle | null`.
 */
export const PAGE_BORDER_STYLES = BORDER_STYLE_KEYS;

/**
 * Phase 1 plan-aligned alias for `BorderStyleKey`.
 * See `PAGE_BORDER_STYLES` above for context.
 */
export type PageBorderStyle = BorderStyleKey;

/**
 * FE-safe display data without prompts.
 * Use this on the frontend for showing options to users.
 */
export const BORDER_STYLE_DISPLAY: Record<BorderStyleKey, BorderStyleDisplay> =
  Object.fromEntries(
    BORDER_STYLE_KEYS.map((key) => [
      key,
      {
        title: BORDER_STYLES[key].title,
        description: BORDER_STYLES[key].description,
        emoji: BORDER_STYLES[key].emoji,
      },
    ])
  ) as Record<BorderStyleKey, BorderStyleDisplay>;

/**
 * Get border style prompt by key (BE use only).
 */
export function getBorderStylePrompt(key: BorderStyleKey): string {
  return BORDER_STYLES[key].borderPrompt;
}

/**
 * Get border style display data by key (FE-safe).
 */
export function getBorderStyleDisplay(key: BorderStyleKey): BorderStyleDisplay {
  return BORDER_STYLE_DISPLAY[key];
}
